import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Button, MenuItem, Typography } from '@material-ui/core'
import Menus from 'app/componentV4/Menu/Menus'
import useStyles from './styles'
import { usePagination, DOTS } from './usePagination'

const Pagination = (props) => {
  const {
    lastPage,
    pageSize,
    className,
    totalCount,
    dataLength,
    currentPage,
    onPageChange,
    onPerPageChange,
    isPerpage = true,
    siblingCount = 1,
    disabled = false,
  } = props

  const classes = useStyles()
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  })
  // Added extra pagination in dev for quick operation test
  const isDevEnv = process.env.REACT_APP_ENV === 'dev'
  const perPageList = isDevEnv
    ? [5, 10, 20, 25, 50, 100, 200, 500, 1000, 3000]
    : [5, 10, 20, 25, 50, 100, 200]

  // Hooks must be defined before any return statement
  const [anchorEl, setAnchorEl] = useState(null)
  const [numberPage, setNumberPage] = useState({
    fromNumber: 1,
    toNumber: 10,
    total: 10,
  })

  // Always call hooks like useEffect before any conditional return
  useEffect(() => {
    const isTotalCount = dataLength === totalCount
    const isFirstPageAndTotalCount = isTotalCount ? dataLength : pageSize
    const isAnothetPageAndTotalCount = isTotalCount
      ? dataLength
      : (currentPage - 1) * pageSize + dataLength

    setNumberPage({
      fromNumber: currentPage === 1 ? 1 : (currentPage - 1) * pageSize + 1,
      toNumber:
        currentPage === 1
          ? isFirstPageAndTotalCount
          : isAnothetPageAndTotalCount,
      total: totalCount,
    })
  }, [currentPage, pageSize, dataLength, totalCount])

  // Early return but after hooks have been declared
  if ((currentPage === 0 || paginationRange.length < 2) && !isPerpage) {
    return null
  }

  const onNext = () => {
    if (disabled) return
    else onPageChange(currentPage + 1)
  }

  const onPrevious = () => {
    if (disabled) return
    else onPageChange(currentPage - 1)
  }

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  return (
    <div
      className={clsx('flex justify-end my-10 w-full', {
        [className]: className,
      })}
    >
      <div className="flex w-5/6 justify-center mr-10">
        <ul className={clsx(classes.paginationContainer)}>
          <li
            className={clsx('paginationItem', {
              disabled: currentPage === 1,
            })}
            onClick={onPrevious}
          >
            <div className="arrow left" />
          </li>
          {paginationRange.map((pageNumber, index) => {
            if (pageNumber === DOTS) {
              return (
                <li key={`page-${index + 1}`} className="paginationItem dots">
                  &#8230;
                </li>
              )
            }

            return (
              <li
                key={`page-${index + 1}`}
                className={clsx('paginationItem', {
                  selected: pageNumber === currentPage,
                })}
                onClick={() => {
                  if (disabled || pageNumber === currentPage) return
                  else onPageChange(pageNumber)
                }}
              >
                {pageNumber}
              </li>
            )
          })}
          <li
            className={clsx('paginationItem', {
              disabled: currentPage === lastPage,
            })}
            onClick={onNext}
          >
            <div className="arrow right" />
          </li>
        </ul>
        {isPerpage && (
          <div className={clsx(classes.buttonPerPage, 'flex items-center')}>
            <Button
              aria-controls="select-perpage"
              aria-owns={anchorEl ? 'select-perpage' : null}
              variant="outlined"
              onClick={openMenu}
              className="border border-grey-400 text-grey-400 text-14 font-400 lowercase"
              disabled={disabled}
            >
              <FormattedMessage
                id="label.perPage"
                values={{
                  pageNumber: pageSize,
                }}
              />
            </Button>
            <Menus
              id="select-perpage"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
            >
              {perPageList.map((value) => (
                <MenuItem
                  key={`${value}-per-page`}
                  onClick={() => {
                    onPerPageChange(value)
                    closeMenu()
                  }}
                >
                  <Typography className="text-14 font-400 text-primary">
                    <FormattedMessage
                      id="label.perPage"
                      values={{
                        pageNumber: value,
                      }}
                    />
                  </Typography>
                </MenuItem>
              ))}
            </Menus>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end w-1/6">
        <Typography className="font-bold text-grey-300 text-14">
          <FormattedMessage
            id="label.numberOfItem"
            values={{
              fromNumber: numberPage.fromNumber.toLocaleString(),
              toNumber: numberPage.toNumber.toLocaleString(),
              total: numberPage.total.toLocaleString(),
            }}
          />
        </Typography>
      </div>
    </div>
  )
}

Pagination.propTypes = {
  disabled: PropTypes.bool,
  isPerpage: PropTypes.bool,
  lastPage: PropTypes.number,
  className: PropTypes.string,
  siblingCount: PropTypes.number,
  onPerPageChange: PropTypes.func,
  pageSize: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  dataLength: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
}

export default Pagination
